import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../../theme-bspi.css';

const LOGO = '/logo-star.png';
const FOUNDER_IMG = 'https://customer-assets.emergentagent.com/job_c23bcffd-0630-46f5-9619-ffb2776c1de5/artifacts/ugqa12qn_IAM_Assets.jpg';
const PIMS_LOGO = 'https://res.cloudinary.com/dp6nne059/image/upload/v1773333819/Pims_Logo_Blue_pyaxwu.jpg';
const API = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// Site header — matches reference exactly (sticky, blurred, star 54px + brand
// stack). Nav links: Approach · Services · What we buy · Insights · Contact.
// Gold CTA button "Tell us about a property".
// ---------------------------------------------------------------------------
export const BspiHeader = () => {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  // Route-aware: on the homepage we smooth-scroll to the section. On any other
  // page (legal, insights, 404) the same links navigate to /#anchor so the
  // homepage picks up the hash on mount and scrolls to the right section.
  const isHome = () => (typeof window !== 'undefined' && window.location.pathname === '/');
  const scrollTo = useCallback((id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);
  const closeMenu = () => setMenuOpen(false);
  const goSection = (id) => (e) => {
    e.preventDefault();
    closeMenu();
    if (isHome()) {
      // Update the hash in the URL so it's shareable + back-button friendly,
      // then scroll. We use history.replaceState so we don't leave every
      // section click in the history stack.
      if (window.location.hash !== `#${id}`) {
        window.history.replaceState(null, '', `#${id}`);
      }
      scrollTo(id);
    } else {
      navigate(`/#${id}`);
    }
  };
  const goHome = (e) => {
    e.preventDefault();
    closeMenu();
    if (isHome()) {
      scrollTo('top');
    } else {
      navigate('/');
    }
  };
  // ESC closes the drawer; also lock body scroll while it's open.
  useEffect(() => {
    if (!menuOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') closeMenu(); };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prev;
      window.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);
  const home = isHome();
  return (
    <header className="site-hdr">
      <div className="wrap bar">
        <a className="brand" href={home ? '#top' : '/'} aria-label="Black Star PI home"
           onClick={goHome} data-testid="header-brand">
          <span className="logo"><img src={LOGO} alt="" width="54" height="54" /></span>
          <span className="bt">
            <b>BLACK STAR PI</b>
            <small>ACQUIRE<i>·</i>OPTIMISE<i>·</i>MULTIPLY<i>·</i></small>
          </span>
        </a>
        <nav className="links" aria-label="Main">
          <a href={home ? '#approach' : '/#approach'} onClick={goSection('approach')} data-testid="nav-approach">Approach</a>
          <a href={home ? '#services' : '/#services'} onClick={goSection('services')} data-testid="nav-services">Services</a>
          <a href={home ? '#buy' : '/#buy'}          onClick={goSection('buy')}      data-testid="nav-buy">What we buy</a>
          <a href="/insights" onClick={(e) => { e.preventDefault(); closeMenu(); navigate('/insights'); }} data-testid="nav-insights">Insights</a>
          <a href={home ? '#contact' : '/#contact'} onClick={goSection('contact')}  data-testid="nav-contact">Contact</a>
        </nav>
        <a className="btn btn-gold desktop-cta"
           href={home ? '#contact' : '/#contact'}
           onClick={goSection('contact')}
           data-testid="header-contact-cta">
          Tell us about a property
        </a>
        {/* Hamburger — visible below 1200px only */}
        <button
          type="button"
          className={`burger${menuOpen ? ' is-open' : ''}`}
          aria-label={menuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={menuOpen}
          aria-controls="mobile-drawer"
          onClick={() => setMenuOpen((v) => !v)}
          data-testid="menu-toggle"
        >
          <span></span><span></span><span></span>
        </button>
      </div>
      {/* Drawer + backdrop portalled to <body> so the header's backdrop-filter
          doesn't trap them as a containing block. */}
      {typeof document !== 'undefined' && createPortal(
        <>
          <div
            className={`bspi-drawer-backdrop${menuOpen ? ' is-open' : ''}`}
            onClick={closeMenu}
            aria-hidden={!menuOpen}
          />
          <aside
            id="mobile-drawer"
            className={`bspi-drawer${menuOpen ? ' is-open' : ''}`}
            role="dialog"
            aria-modal="true"
            aria-hidden={!menuOpen}
            data-testid="mobile-drawer"
          >
            <button
              type="button"
              className="bspi-drawer-close"
              aria-label="Close menu"
              onClick={closeMenu}
              data-testid="drawer-close"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                <line x1="6" y1="6" x2="18" y2="18" />
                <line x1="18" y1="6" x2="6" y2="18" />
              </svg>
            </button>
            <nav className="bspi-drawer-links" aria-label="Mobile main">
              <a href={home ? '#approach' : '/#approach'} onClick={goSection('approach')} data-testid="drawer-nav-approach">Approach</a>
              <a href={home ? '#services' : '/#services'} onClick={goSection('services')} data-testid="drawer-nav-services">Services</a>
              <a href={home ? '#buy' : '/#buy'}          onClick={goSection('buy')}      data-testid="drawer-nav-buy">What we buy</a>
              <a href="/insights" onClick={(e) => { e.preventDefault(); closeMenu(); navigate('/insights'); }} data-testid="drawer-nav-insights">Insights</a>
              <a href={home ? '#contact' : '/#contact'} onClick={goSection('contact')}  data-testid="drawer-nav-contact">Contact</a>
            </nav>
            <a className="btn btn-gold bspi-drawer-cta"
               href={home ? '#contact' : '/#contact'}
               onClick={goSection('contact')}
               data-testid="drawer-contact-cta">
              Tell us about a property →
            </a>
          </aside>
        </>,
        document.body,
      )}
    </header>
  );
};

// ---------------------------------------------------------------------------
// Hero — pixel-matched to the reference. Headline visible instantly (no rise
// animation on the copy so crawlers and impatient viewports get the text).
// ---------------------------------------------------------------------------
// Robust hash-scroll: when the homepage loads with #approach / #services /
// #buy / #contact / #situations in the URL — either from a menu link on
// another page or when the address is opened directly — smooth-scroll to
// the section once the layout has settled. Sticky-header offset comes from
// CSS `scroll-margin-top: 90px` in theme-bspi.css.
// Polls up to ~2s for the target element to be present with a real height
// (guards against fonts / images pushing layout after first paint).
// ---------------------------------------------------------------------------
export const useHashScrollOnMount = () => {
  useEffect(() => {
    const raw = window.location.hash?.replace('#', '');
    if (!raw) return;
    // Disable the browser's native jump-to-anchor so we can drive it.
    if ('scrollRestoration' in window.history) {
      const prev = window.history.scrollRestoration;
      window.history.scrollRestoration = 'manual';
      // Restore on unmount.
      return () => { window.history.scrollRestoration = prev; };
    }
  }, []);

  useEffect(() => {
    const raw = window.location.hash?.replace('#', '');
    if (!raw) return;
    let cancelled = false;
    const startedAt = performance.now();
    const attempt = () => {
      if (cancelled) return;
      const el = document.getElementById(raw);
      // Wait for the target section to have real content laid out — otherwise
      // a race with async image loads can leave scrolling short.
      if (el && el.getBoundingClientRect().height > 20) {
        // Small extra tick lets the browser paint before we scroll.
        window.requestAnimationFrame(() => {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        });
        return;
      }
      if (performance.now() - startedAt < 2500) {
        setTimeout(attempt, 60);
      }
    };
    // Kick after the first paint so React children are in the DOM.
    setTimeout(attempt, 30);
    return () => { cancelled = true; };
  }, []);
};

export const BspiHero = () => {
  useHashScrollOnMount();
  useEffect(() => {
    document.title = 'Black Star PI | Creative property acquisition and development';
  }, []);
  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  return (
    <section className="hero" style={{ padding: 0 }} id="top">
      <div className="sky" aria-hidden="true"><i></i><i></i><i></i></div>
      <div className="shapes sky" aria-hidden="true">
        <span className="s1"></span>
        <span className="s2"></span>
        <span className="s3"></span>
        <span className="s4"></span>
        <span className="s5"></span>
      </div>
      <div className="wrap">
        <div className="emblem" aria-hidden="true">
          <span className="orbit"></span>
          <span className="orbit o2"></span>
          <img src={LOGO} alt="" />
        </div>
        <div className="tag">
          <span>Acquire<i>.</i></span>
          <span>Optimise<i>.</i></span>
          <span>Multiply<i>.</i></span>
        </div>
        <span className="eyebrow">
          Creative acquisition and development <span className="dot">·</span> Multi-jurisdiction
        </span>
        <h1 data-testid="hero-headline">
          We buy the property <em>others walk away from.</em>
        </h1>
        <p className="lead">
          Underperforming, hard to sell, poorly structured or simply not reaching its potential. Black Star PI finds the structure that works for you, then creates the value the property was missing.
        </p>
        <div className="ctas">
          <a className="btn btn-gold" href="#contact"
             onClick={(e) => { e.preventDefault(); scrollTo('contact'); }}
             data-testid="hero-primary-cta">
            Tell us about a property →
          </a>
          <a className="btn btn-line" href="#approach"
             onClick={(e) => { e.preventDefault(); scrollTo('approach'); }}
             data-testid="hero-secondary-cta">
            How we work
          </a>
        </div>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Approach — four numbered steps on a hairline divider row.
// ---------------------------------------------------------------------------
export const BspiApproach = () => (
  <section className="approach" id="approach">
    <div className="wrap">
      <div className="head">
        <span className="eyebrow">Our approach</span>
        <h2>Run with the discipline of a major infrastructure programme.</h2>
      </div>
      <div className="steps">
        <div className="step"><span className="n">01</span><h3>Understand the asset</h3><p>What it is, what it earns, what it could be.</p></div>
        <div className="step"><span className="n">02</span><h3>Identify the constraint</h3><p>The one thing holding the property, or its owner, back.</p></div>
        <div className="step"><span className="n">03</span><h3>Structure the right intervention</h3><p>A purchase, a lease, a delayed completion or a development plan, whichever fits.</p></div>
        <div className="step"><span className="n">04</span><h3>Create long-term value</h3><p>Optimise, reposition and hold what the numbers support.</p></div>
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// Situations — where we help. Two-column list on deep-green background.
// ---------------------------------------------------------------------------
export const BspiSituations = () => (
  <section className="situations">
    <div className="wrap sit-grid">
      <div className="head" style={{ marginBottom: 0 }}>
        <span className="eyebrow">Where we help</span>
        <h2>Situations that need more than a price.</h2>
        <p>These are the properties where a creative structure and an engineer's eye make the biggest difference, for the owner and for the building.</p>
      </div>
      <ul className="sit-list">
        <li>Hard to sell <span>On the market too long</span></li>
        <li>Underperforming <span>Earning less than it should</span></li>
        <li>Empty or unwanted <span>A cost, not an asset</span></li>
        <li>Poorly structured <span>Title, lease or layout issues</span></li>
        <li>Needs time <span>Sell now, complete later</span></li>
      </ul>
    </div>
  </section>
);


// ---------------------------------------------------------------------------
// Services — 2x2 grid of service articles + gold promise strip.
// ---------------------------------------------------------------------------
export const BspiServices = () => (
  <section className="services" id="services">
    <div className="wrap">
      <div className="head">
        <span className="eyebrow">Services</span>
        <h2>Four ways to solve it.</h2>
      </div>
      <div className="svc">
        <article>
          <span className="k">Creative Acquisitions</span>
          <h3>We buy property that won't sell the usual way.</h3>
          <p>Buy now, buy later, or on terms built around your timing, your mortgage and your plans. That includes renting the property from you now and buying it later at a price agreed today.</p>
          <span className="fine">Direct purchase · Purchase lease option · Delayed completion · Vendor-led terms</span>
        </article>
        <article>
          <span className="k">Owner and Landlord Solutions</span>
          <h3>We take on property that has become a problem.</h3>
          <p>Empty, underperforming, badly let or simply unwanted. We can manage it, lease it, or set it on a route to an eventual sale, whichever solves the problem.</p>
          <span className="fine">Management-led arrangements · Lease structures · Route to acquisition</span>
        </article>
        <article>
          <span className="k">Development and Value Creation</span>
          <h3>We create value others miss.</h3>
          <p>Refurbishment, conversion, redevelopment and change of use, turning tired or awkward buildings into property worth more.</p>
          <span className="fine">Refurbishment · Conversion · Redevelopment · Repositioning · Change of use</span>
        </article>
        <article>
          <span className="k">What We Buy</span>
          <h3>Property where the numbers stack up.</h3>
          <p>Residential, commercial and mixed-use property, judged on its fundamentals and its long-term value.</p>
          <span className="fine">Residential · Commercial · Mixed-use</span>
        </article>
      </div>
      <div className="promise">
        <img src={LOGO} alt="" />
        <p>Every agreement is drafted by solicitors, and you always have your own.</p>
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// What we buy — 3-column list on deep-green with a footnote.
// ---------------------------------------------------------------------------
export const BspiWhatWeBuy = () => (
  <section className="buy" id="buy">
    <div className="wrap">
      <div className="head">
        <span className="eyebrow">What we buy</span>
        <h2>Judged on fundamentals, never on trends.</h2>
      </div>
      <div className="buy-grid">
        <div>
          <h3>Residential</h3>
          <p>Houses and flats, including property that needs work, time or a different structure.</p>
        </div>
        <div>
          <h3>Commercial</h3>
          <p>Buildings with a use, a lease or a layout that is holding their value back.</p>
        </div>
        <div>
          <h3>Mixed-use</h3>
          <p>Shops with space above, and buildings that can do more than one job.</p>
        </div>
      </div>
      <p className="where">Where: <b>multi-jurisdiction</b> · based in Edinburgh, United Kingdom</p>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// Founder — photo + quote + credentials pills.
// ---------------------------------------------------------------------------
export const BspiFounder = () => (
  <section className="founder" id="founder">
    <div className="wrap f-grid">
      <div className="f-photo">
        <img src={FOUNDER_IMG} alt="Kingkof Kofi Opoku-Ohemeng, Founder and CEO of Black Star PI" />
      </div>
      <div className="f-body">
        <span className="eyebrow">Why Black Star PI</span>
        <blockquote>
          &ldquo;Property can be approached with the same discipline I bring to major infrastructure programmes: understand the asset, identify the constraint, structure the right intervention and <em>create long-term value.</em>&rdquo;
        </blockquote>
        <p>I'm drawn to property that is underperforming, difficult to sell, poorly structured or not reaching its potential, because that is where intelligent acquisition, optimisation and repositioning make the biggest difference.</p>
        <p className="name">Kingkof Kofi Opoku-Ohemeng
          <span>Founder and CEO · over two decades delivering infrastructure and asset programmes across transport, aviation, ports, energy, utilities and buildings</span>
        </p>
        <div className="creds" aria-label="Professional credentials">
          <span>EUR ING</span><span>FIAM</span><span>CEng</span><span>MCIOB</span><span>CIWFM</span><span>ISO 55001</span>
        </div>
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// Fair questions — 3-column reassurance block.
// ---------------------------------------------------------------------------
export const BspiDoubts = () => (
  <section className="doubts">
    <div className="wrap">
      <div className="head">
        <span className="eyebrow">Before you get in touch</span>
        <h2>Fair questions.</h2>
      </div>
      <div className="qa">
        <div>
          <h3>Does it cost anything to ask?</h3>
          <p>No. Tell us about the property and we will tell you honestly whether we can help, and how.</p>
        </div>
        <div>
          <h3>Will I be pressured?</h3>
          <p>No. You take the time you need, and nothing moves until you are ready.</p>
        </div>
        <div>
          <h3>Who draws up the agreement?</h3>
          <p>Solicitors do, and you always have your own. You never sign anything home-made.</p>
        </div>
      </div>
    </div>
  </section>
);

// ---------------------------------------------------------------------------
// Insights strip — pulls the latest published insight; falls back to a
// static teaser line if none exist yet.
// ---------------------------------------------------------------------------
export const BspiInsightsStrip = () => {
  const [latest, setLatest] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/public/insights`)
      .then((r) => { if (!cancelled) setLatest((r.data || [])[0] || null); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const title = latest?.title || 'The Liquidity Illusion';
  const standfirst = latest?.standfirst || 'Why property funds must stop promising faster exits than their assets can deliver.';
  const href = latest?.slug ? `/insights/${latest.slug}` : '/insights';
  return (
    <section className="insights" id="insights">
      <div className="wrap">
        <div className="ins">
          <div>
            <span className="eyebrow">Insights</span>
            <h3 data-testid="insights-strip-title">{title}</h3>
            <p>{standfirst}</p>
          </div>
          <a className="more" href={href} data-testid="insights-strip-more"
             onClick={(e) => { e.preventDefault(); navigate(href); }}>
            Read our thinking →
          </a>
        </div>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Close + contact form — matches reference. Wired to POST /api/contact.
// Situation dropdown maps to investment_interest. Phone + location persist.
// Reply line: "We reply to every enquiry within two working days."
// ---------------------------------------------------------------------------
export const BspiCloseContact = () => {
  const [form, setForm] = useState({
    name: '', email: '', phone: '', location: '',
    situation: '', message: '', consent: false,
  });
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const [errorMsg, setErrorMsg] = useState('');

  const handle = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) return;
    setStatus('sending'); setErrorMsg('');
    try {
      await axios.post(`${API}/api/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        phone: form.phone.trim(),
        location: form.location.trim(),
        investment_interest: form.situation || 'Something else',
        message: form.message.trim(),
      });
      setStatus('sent');
      setForm({ name: '', email: '', phone: '', location: '', situation: '', message: '', consent: false });
    } catch (err) {
      setStatus('error');
      setErrorMsg(err?.response?.data?.detail || 'Could not send. Please try again in a moment.');
    }
  };

  return (
    <section className="close" id="contact">
      <div className="wrap c-grid">
        <div className="side">
          <span className="eyebrow">Get in touch</span>
          <h2>Tell us about a property <em>or real estate.</em></h2>
          <p>A few lines is enough: where it is, what it is, and what's not working. We'll come back to you personally.</p>
          <div className="contact-lines">
            <a href="mailto:info@black-starpi.com">info@black-starpi.com</a>
            <a href="tel:+447759342229">+44 7759 342229</a>
            <a href="tel:+441313811989">+44 131 381 1989</a>
          </div>
        </div>
        <form id="enquiry" onSubmit={submit} noValidate data-testid="contact-form">
          <div className="f">
            <label htmlFor="nm">Name</label>
            <input id="nm" name="name" autoComplete="name" required
                   value={form.name} onChange={handle('name')} data-testid="contact-name" />
          </div>
          <div className="f">
            <label htmlFor="em">Email</label>
            <input id="em" name="email" type="email" autoComplete="email" required
                   value={form.email} onChange={handle('email')} data-testid="contact-email" />
          </div>
          <div className="f">
            <label htmlFor="ph">Phone (optional)</label>
            <input id="ph" name="phone" type="tel" autoComplete="tel"
                   value={form.phone} onChange={handle('phone')} data-testid="contact-phone" />
          </div>
          <div className="f">
            <label htmlFor="loc">Where is the property?</label>
            <input id="loc" name="location" placeholder="Town or postcode"
                   value={form.location} onChange={handle('location')} data-testid="contact-location" />
          </div>
          <div className="f full">
            <label htmlFor="sit">What best describes it?</label>
            <select id="sit" name="situation"
                    value={form.situation} onChange={handle('situation')} data-testid="contact-situation">
              <option value="">Choose one</option>
              <option>Hard to sell</option>
              <option>Underperforming</option>
              <option>Empty or unwanted</option>
              <option>Poorly structured</option>
              <option>Development potential</option>
              <option>I'm an agent or introducer</option>
              <option>Something else</option>
            </select>
          </div>
          <div className="f full">
            <label htmlFor="msg">Tell us about it</label>
            <textarea id="msg" name="message"
                      value={form.message} onChange={handle('message')} data-testid="contact-message" />
          </div>
          <label className="consent" htmlFor="ok">
            <input id="ok" type="checkbox" required
                   checked={form.consent} onChange={handle('consent')} data-testid="contact-consent" />
            <span>I agree to Black Star PI using these details to reply to my enquiry, as set out in the <a href="/privacy-policy">privacy policy</a>.</span>
          </label>
          <button className="btn btn-gold" type="submit"
                  disabled={status === 'sending'} data-testid="contact-submit">
            {status === 'sending' ? 'Sending…' : 'Send property details →'}
          </button>
          {status === 'sent' && (
            <p className="sent" data-testid="contact-sent">Thank you. We have your details and will come back to you personally within two working days.</p>
          )}
          {status === 'error' && (
            <p className="sent" style={{ color: '#e46a6a' }} data-testid="contact-error">{errorMsg}</p>
          )}
          <p className="reply">We reply to every enquiry within two working days.</p>
        </form>
      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// Footer — 4-column, with PIMS affiliation and legal navigation.
// Section-anchor links (#approach, #services, #buy, #contact) work from any
// page: if we're already on the homepage they smooth-scroll; if not, we
// navigate to `/#anchor` and let the homepage handle the scroll on mount.
// ---------------------------------------------------------------------------
export const BspiFooter = () => {
  const navigate = useNavigate();
  const goSection = (id) => (e) => {
    e.preventDefault();
    if (window.location.pathname === '/') {
      const el = document.getElementById(id);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      navigate(`/#${id}`);
    }
  };
  return (
    <footer>
      <div className="wrap">
        <div className="ft">
          <div>
            <a className="brand" href="/" aria-label="Black Star PI home"
               onClick={(e) => { e.preventDefault(); navigate('/'); }}>
              <span className="logo"><img src={LOGO} alt="" /></span>
              <span className="bt">
                <b>BLACK STAR PI</b>
                <small>ACQUIRE<i>·</i>OPTIMISE<i>·</i>MULTIPLY<i>·</i></small>
              </span>
            </a>
            <p className="blurb">Creative acquisition and development.</p>
          </div>
          <div>
            <h4>Explore</h4>
            <ul>
              <li><a href="/" onClick={(e) => { e.preventDefault(); navigate('/'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}>Home</a></li>
              <li><a href="/#approach" onClick={goSection('approach')}>Approach</a></li>
              <li><a href="/#services" onClick={goSection('services')}>Services</a></li>
              <li><a href="/#buy" onClick={goSection('buy')}>What we buy</a></li>
              <li><a href="/insights" onClick={(e) => { e.preventDefault(); navigate('/insights'); }}>Insights</a></li>
              <li><a href="/#contact" onClick={goSection('contact')}>Contact</a></li>
            </ul>
          </div>
          <div>
            <h4>Contact</h4>
            <ul>
              <li><a href="mailto:info@black-starpi.com">info@black-starpi.com</a></li>
              <li><a href="tel:+447759342229">+44 7759 342229</a></li>
              <li><a href="tel:+441313811989">+44 131 381 1989</a></li>
            </ul>
            <div className="socials" role="group" aria-label="Social channels">
              <a
                className="soc"
                href="https://www.linkedin.com/in/kingkof-kofi-o-091b9557"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                data-testid="footer-linkedin"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                  <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9h4v12H3V9zm7 0h3.8v1.71h.05c.53-1 1.83-2.05 3.77-2.05 4.03 0 4.78 2.65 4.78 6.1V21h-4v-5.4c0-1.29-.02-2.95-1.8-2.95-1.8 0-2.07 1.4-2.07 2.86V21h-4V9z"/>
                </svg>
              </a>
              <a
                className="soc"
                href="https://wa.me/447759342229"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="WhatsApp"
                data-testid="footer-whatsapp"
              >
                <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.198.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.611-.916-2.207-.242-.579-.487-.5-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.875 1.213 3.074c.149.198 2.095 3.2 5.077 4.487.71.306 1.262.489 1.694.626.712.226 1.36.194 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.29.173-1.413-.074-.124-.272-.198-.57-.347M12.05 2C6.495 2 2 6.495 2 12.05c0 1.76.456 3.487 1.323 5.01L2 22l4.98-1.302a10.04 10.04 0 0 0 5.07 1.383h.005c5.554 0 10.05-4.496 10.05-10.05 0-2.687-1.046-5.213-2.945-7.113A9.97 9.97 0 0 0 12.05 2m-.001 18.135h-.004a8.16 8.16 0 0 1-4.156-1.14l-.298-.177-3.09.807.826-3.011-.194-.31a8.14 8.14 0 0 1-1.249-4.35c.002-4.503 3.667-8.167 8.172-8.167a8.11 8.11 0 0 1 5.775 2.394 8.12 8.12 0 0 1 2.393 5.78c-.002 4.502-3.667 8.174-8.175 8.174"/>
                </svg>
              </a>
            </div>
          </div>
          <div className="aff">
            <h4>Affiliations</h4>
            <a href="https://www.thepims.co.uk/" target="_blank" rel="noreferrer"
               title="PIMS, Property Investors Mediation Service">
              <img src={PIMS_LOGO} alt="PIMS, Property Investors Mediation Service" />
            </a>
          </div>
        </div>
        <div className="legal">
          <span>© {new Date().getFullYear()} Black Star PI Ltd. All rights reserved. · United Kingdom</span>
          <nav aria-label="Legal">
            <a href="/privacy-policy" onClick={(e) => { e.preventDefault(); navigate('/privacy-policy'); }}>Privacy Policy</a>
            <a href="/terms-of-service" onClick={(e) => { e.preventDefault(); navigate('/terms-of-service'); }}>Terms of Service</a>
            <a href="/complaints" onClick={(e) => { e.preventDefault(); navigate('/complaints'); }}>Complaints Procedure</a>
          </nav>
        </div>
      </div>
    </footer>
  );
};
